import { Container, Typography, Grid, Box, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import Footer from '../components/Footer';
import { useEffect } from 'react';

const GuaranteeCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.04)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    padding: theme.spacing(3),
  },
}));

const guarantees = [
  {
    title: 'Strategic development',
    description:
      'We approach every site as a long-term investment, aligning land use, entitlement, design, and capital structure from day one.',
  },
  {
    title: 'Uncompromising quality',
    description:
      'From materials to partnerships, we hold each project to a high standard of craftsmanship, performance, and guest experience.',
  },
  {
    title: 'Seamless execution',
    description:
      'We coordinate consultants, agencies, and operators under a single, accountable team, so decisions stay timely and aligned.',
  },
  {
    title: 'Transparent communication',
    description:
      'Investors and partners receive clear, consistent reporting and direct access to leadership at every stage of the project.',
  },
];

const services = [
  {
    title: 'Development Services',
    body: 'End-to-end guidance for complex resort, residential, and mixed-use projects in environmentally sensitive locations.',
    bullets: [
      'Master land use planning and entitlement strategy',
      'Infrastructure and renewable energy design coordination',
      'Capital stack assembly and investor relations',
      'Horizontal and vertical construction oversight',
    ],
  },
  {
    title: 'Brokerage Services',
    body: 'Senior-level sales and marketing expertise for pre-construction and stabilized assets across the Tahoe Basin and beyond.',
    bullets: [
      'Pre-sales strategy and launch planning',
      'Luxury residential, multi-family, and hospitality sales',
      'National and international referral network',
      'Velocity management and pricing strategy',
    ],
  },
  {
    title: 'Consulting & Advisory',
    body: 'Independent perspective on feasibility, positioning, and financial performance for new and existing projects.',
    bullets: [
      'Feasibility, pro forma, and sensitivity analysis',
      'Resort and residence club programing',
      'Workout strategies for lenders and owners',
      'Capital markets and opportunity zone advisory',
    ],
  },
  {
    title: 'Project Management',
    body: 'Hands-on leadership to keep schedules, budgets, and teams aligned from concept through opening.',
    bullets: [
      'Owner’s representative services',
      'Budget, schedule, and risk management',
      'Stakeholder and agency coordination',
      'Operational handover and performance tracking',
    ],
  },
];

export default function Services() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container sx={{ py: 10 }}>
        <Grid container spacing={6} sx={{ mb: 6 }}>
          <Grid item xs={12} md={7}>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Services
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              color="primary.main"
              sx={{
                fontSize: {
                  xs: '2rem',
                  sm: '2.4rem',
                  md: '2.9rem',
                },
              }}
            >
              Our Services & Guarantees.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640 }}>
              We combine deep local knowledge, disciplined underwriting, and hands-on execution to deliver projects that
              perform for investors and feel timeless for owners and guests.
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 8 }}>
          {guarantees.map((item) => (
            <GuaranteeCard key={item.title}>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.16em', mb: 1 }}
                >
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.description}
                </Typography>
              </Box>
              {/* <Button variant="text" color="primary" sx={{ whiteSpace: 'nowrap' }}>
                Learn more
              </Button> */}
            </GuaranteeCard>
          ))}
        </Box>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {services.map(service => (
            <Grid item xs={12} md={6} key={service.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  border: theme => `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                  height: '100%',
                }}
              >
                <Typography variant="h5" gutterBottom>
                  {service.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  {service.body}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                  {service.bullets.map(point => (
                    <Typography
                      component="li"
                      key={point}
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      {point}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            borderRadius: 3,
            bgcolor: 'primary.main',
            color: 'common.white',
            px: { xs: 3, md: 5 },
            py: { xs: 4, md: 5 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ mb: 1 }}>
              Ready to discuss your project?
            </Typography>
            <Typography variant="body2">
              Share your vision and we&apos;ll outline potential paths forward—from entitlement feasibility to capital
              structure and sales strategy.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="inherit"
            sx={{
              color: 'primary.main',
            }}
            href="/contact"
          >
            Contact us
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}

